import { useFocusRing } from '@react-aria/focus'
import { useNumberFormatter } from '@react-aria/i18n'
import { useSlider, useSliderThumb } from '@react-aria/slider'
import { mergeProps } from '@react-aria/utils'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { SliderState, useSliderState } from '@react-stately/slider'
import { AriaSliderProps, AriaSliderThumbProps } from '@react-types/slider'
import React, { useRef } from 'react'
import Styles from './ColorsPanel.module.scss'
import cx from 'classnames'
import { GradientStop } from 'components/VisualStudioPlayground/VisualEditor/Types'

interface GradientSliderProps extends AriaSliderProps {
  stops: Array<GradientStop>
}

interface ThumbProps extends AriaSliderThumbProps {
  trackRef: React.RefObject<HTMLDivElement>
  state: SliderState
  name?: string
  stop: GradientStop
}

const Thumb = (props: ThumbProps) => {
  const { state, trackRef, index, name, stop } = props
  const inputRef = useRef(null)
  const { thumbProps, inputProps, isDragging } = useSliderThumb(
    {
      index,
      trackRef,
      inputRef,
      name,
    },
    state,
  )

  const { focusProps } = useFocusRing()

  return (
    <div
      {...thumbProps}
      className={cx(Styles.gradientThumbContainer, Styles.gradientThumb, {
        [Styles.dragging]: isDragging,
        [Styles.chequered]: stop.opacity < 1,
      })}
    >
      <div style={{ background: stop.color, opacity: stop.opacity }} className={Styles.gradientThumb}>
        <VisuallyHidden>
          <input ref={inputRef} {...mergeProps(inputProps, focusProps)} />
        </VisuallyHidden>
      </div>
    </div>
  )
}

export const GradientStopSlider = (props: GradientSliderProps) => {
  let trackRef = React.useRef(null)

  let numberFormatter = useNumberFormatter()
  let state = useSliderState({ ...props, numberFormatter })
  let { groupProps, trackProps } = useSlider(props, state, trackRef)

  return (
    <div {...groupProps} className={Styles.gradientSliderContainer}>
      <div {...trackProps} ref={trackRef}>
        {props.stops.map((stop, index) => (
          <Thumb key={index} index={index} state={state} trackRef={trackRef} stop={stop} />
        ))}
      </div>
    </div>
  )
}
